import { useEffect, useMemo, useState } from 'react';
import L from 'leaflet';
import {
  worldToMapPixelPosition,
  type CollectibleKind,
  type CollectibleLayerStatus,
  type PlayerMapMarker,
  type PlayerSnapshot,
  type RailSignalSnapshot,
  type RailStationSnapshot,
  type RailTrackSegment,
  type RailTrainSnapshot,
  type ResourcePurity as ApiResourcePurity,
  type ResourceType,
  type ResourceUsageState,
} from '@satismap/shared';
import {
  ImageOverlay,
  MapContainer,
  Marker,
  CircleMarker,
  Polyline,
  Tooltip,
  useMap,
} from 'react-leaflet';
import {
  Check,
  ChevronDown,
  CircleHelp,
  Layers3,
  LocateFixed,
  Map as MapIcon,
  MapPin,
  Menu,
  PanelLeftClose,
  Pickaxe,
  RefreshCw,
  Search,
  SlidersHorizontal,
  TrainFront,
  TrainTrack,
  Users,
  X,
} from 'lucide-react';
import { resourceColors } from './data/resourceColors';
import { LoginScreen } from './components/LoginScreen';
import { useAuthentication } from './hooks/use-authentication';
import { useSatisMapData } from './hooks/use-satismap-data';
import type { ResourceKind, ResourceNode, ResourcePurity, ResourceState, ViewMode } from './types';

const MAP_SIZE = 5000;
const MAP_BOUNDS: L.LatLngBoundsExpression = [[0, 0], [MAP_SIZE, MAP_SIZE]];
const RESOURCE_KINDS = Object.keys(resourceColors) as ResourceKind[];
const PURITIES: ResourcePurity[] = ['Unrein', 'Normal', 'Rein'];
const STATES: ResourceState[] = ['Frei', 'Belegt', 'Unbekannt'];
const ROUTE_COLORS = ['#e9ad35', '#70b7e6', '#d77bd8', '#72c58c', '#e9855d', '#aaa6e8', '#cfcb65', '#5fc5bd', '#e17286', '#9fc66f'];

const RESOURCE_ICON_PATH: Record<ResourceKind, string> = {
  Eisen: '/icons/resource-iron-ore.png',
  Kupfer: '/icons/resource-copper-ore.png',
  Kalkstein: '/icons/resource-limestone.png',
  Kohle: '/icons/resource-coal.png',
  Caterium: '/icons/resource-caterium-ore.png',
  Schwefel: '/icons/resource-sulfur.png',
  Quarz: '/icons/resource-raw-quartz.png',
  Bauxit: '/icons/resource-bauxite.png',
  Uran: '/icons/resource-uranium.png',
  SAM: '/icons/resource-sam.png',
  Rohöl: '/icons/resource-crude-oil.png',
  Wasser: '/icons/resource-water.png',
  Stickstoff: '/icons/resource-nitrogen-gas.png',
  Geysir: '/icons/geyser.png',
};

type PurityByResource = Record<ResourceKind, Set<ResourcePurity>>;

function allResourcePurities(): PurityByResource {
  return Object.fromEntries(
    RESOURCE_KINDS.map((kind) => [kind, new Set(PURITIES)]),
  ) as PurityByResource;
}

const RESOURCE_TYPE_TO_KIND: Record<ResourceType, ResourceKind> = {
  'iron-ore': 'Eisen',
  'copper-ore': 'Kupfer',
  limestone: 'Kalkstein',
  coal: 'Kohle',
  'caterium-ore': 'Caterium',
  'raw-quartz': 'Quarz',
  sulfur: 'Schwefel',
  uranium: 'Uran',
  bauxite: 'Bauxit',
  sam: 'SAM',
  'crude-oil': 'Rohöl',
  water: 'Wasser',
  'nitrogen-gas': 'Stickstoff',
  geyser: 'Geysir',
};

const API_PURITY_TO_LABEL: Record<ApiResourcePurity, ResourcePurity> = {
  impure: 'Unrein',
  normal: 'Normal',
  pure: 'Rein',
};

const API_USAGE_TO_LABEL: Record<ResourceUsageState, ResourceState> = {
  unused: 'Frei',
  used: 'Belegt',
  unknown: 'Unbekannt',
};

const toLatLng = (x: number, y: number): L.LatLngTuple => [MAP_SIZE - y, x];
interface PositionedCollectible {
  id: string;
  kind: CollectibleKind;
  label: string;
  iconPath: string;
  x: number;
  y: number;
  z: number;
}

interface PositionedPlayer extends PlayerSnapshot {
  mapX: number;
  mapY: number;
}

interface PositionedMapMarker extends PlayerMapMarker {
  mapX: number;
  mapY: number;
}

interface PositionedRailStation extends RailStationSnapshot {
  mapX: number;
  mapY: number;
}

interface PositionedRailTrain extends RailTrainSnapshot {
  mapX: number;
  mapY: number;
  color: string;
}

interface PositionedRailSignal extends RailSignalSnapshot {
  mapX: number;
  mapY: number;
}

interface PositionedRailTrack extends RailTrackSegment {
  positions: L.LatLngTuple[];
}

type RailSelection =
  | { kind: 'station'; station: PositionedRailStation }
  | { kind: 'train'; train: PositionedRailTrain };

function MapResize({ panelOpen, detailOpen }: { panelOpen: boolean; detailOpen: boolean }) {
  const map = useMap();
  useEffect(() => {
    const timer = window.setTimeout(() => map.invalidateSize(), 260);
    return () => window.clearTimeout(timer);
  }, [detailOpen, map, panelOpen]);
  return null;
}

function FocusMap({ target }: { target: { x: number; y: number } | null }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(toLatLng(target.x, target.y), 1.25, { duration: 0.7 });
  }, [map, target]);
  return null;
}

function MapModeController({ viewMode, hasTarget }: { viewMode: ViewMode; hasTarget: boolean }) {
  const map = useMap();
  useEffect(() => {
    if (!hasTarget) {
      window.setTimeout(() => map.fitBounds(MAP_BOUNDS, { padding: [34, 34], animate: true }), 40);
    }
  }, [hasTarget, map, viewMode]);
  return null;
}

function CountPill({ children }: { children: React.ReactNode }) {
  return <span className="count-pill">{children}</span>;
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) {
  return (
    <button
      type="button"
      className={`toggle ${checked ? 'is-on' : ''}`}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
    >
      <span />
    </button>
  );
}

function FilterSection({
  title,
  icon,
  count,
  children,
  defaultOpen = true,
}: {
  title: string;
  icon: React.ReactNode;
  count?: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="filter-section">
      <button className="section-heading" type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span className="section-heading-icon">{icon}</span>
        <span>{title}</span>
        {count !== undefined && <CountPill>{count}</CountPill>}
        <ChevronDown size={16} className={open ? 'chevron open' : 'chevron'} />
      </button>
      {open && <div className="section-content">{children}</div>}
    </section>
  );
}

function ResourceFilters({
  selectedKinds,
  puritiesByKind,
  selectedStates,
  setSelectedKinds,
  setPuritiesByKind,
  setSelectedStates,
  counts,
}: {
  selectedKinds: Set<ResourceKind>;
  puritiesByKind: PurityByResource;
  selectedStates: Set<ResourceState>;
  setSelectedKinds: React.Dispatch<React.SetStateAction<Set<ResourceKind>>>;
  setPuritiesByKind: React.Dispatch<React.SetStateAction<PurityByResource>>;
  setSelectedStates: React.Dispatch<React.SetStateAction<Set<ResourceState>>>;
  counts: Record<ResourceKind, number>;
}) {
  const [openPurity, setOpenPurity] = useState<ResourceKind | null>(null);
  const toggleSet = <T,>(setter: React.Dispatch<React.SetStateAction<Set<T>>>, item: T) => {
    setter((current) => {
      const next = new Set(current);
      if (next.has(item)) next.delete(item); else next.add(item);
      return next;
    });
  };

  const togglePurity = (kind: ResourceKind, purity: ResourcePurity) => {
    setPuritiesByKind((current) => {
      const next = { ...current, [kind]: new Set(current[kind]) };
      if (next[kind].has(purity)) next[kind].delete(purity); else next[kind].add(purity);
      return next;
    });
  };

  const puritySummary = (kind: ResourceKind) => {
    const selection = puritiesByKind[kind];
    if (selection.size === 3) return 'Alle';
    if (selection.size === 0) return 'Keine';
    return PURITIES.filter((purity) => selection.has(purity)).join(' · ');
  };

  return (
    <FilterSection title="Erzquellen" icon={<Pickaxe size={17} />} count={selectedKinds.size}>
      <div className="resource-intro">
        <span>Gezielte Auswahl</span>
        {selectedKinds.size > 0 && (
          <button type="button" onClick={() => setSelectedKinds(new Set())}>Alle ausblenden</button>
        )}
      </div>
      <div className="resource-list">
        {RESOURCE_KINDS.map((kind) => {
          const selected = selectedKinds.has(kind);
          return (
            <div
              className={`resource-row ${selected ? 'selected' : ''}`}
              key={kind}
            >
              <button
                className="resource-main"
                type="button"
                onClick={() => toggleSet(setSelectedKinds, kind)}
                aria-pressed={selected}
              >
                <img className="resource-image" src={RESOURCE_ICON_PATH[kind]} alt="" aria-hidden="true" />
                <span className="resource-name">{kind}</span>
                <span className="resource-count">{counts[kind]}</span>
                <span className="check-box">{selected && <Check size={13} strokeWidth={3} />}</span>
              </button>
              <button
                className="purity-trigger"
                type="button"
                aria-expanded={openPurity === kind}
                aria-label={`Reinheit für ${kind}: ${puritySummary(kind)}`}
                onClick={() => setOpenPurity((current) => current === kind ? null : kind)}
              >
                <span>{puritySummary(kind)}</span><ChevronDown size={13} />
              </button>
              {openPurity === kind && (
                <div className="purity-flyout">
                  <div className="purity-flyout-title"><span>Reinheit</span><strong>{kind}</strong></div>
                  {PURITIES.map((purity) => {
                    const active = puritiesByKind[kind].has(purity);
                    return (
                      <button type="button" key={purity} className={active ? 'active' : ''} onClick={() => togglePurity(kind, purity)}>
                        <span className="check-box">{active && <Check size={13} strokeWidth={3} />}</span>
                        <span>{purity}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <fieldset className="filter-group">
        <legend>Status</legend>
        <div className="chip-row">
          {STATES.map((state) => (
            <button
              type="button"
              key={state}
              className={`filter-chip ${selectedStates.has(state) ? 'active' : ''}`}
              onClick={() => toggleSet(setSelectedStates, state)}
            >
              <span className={`status-dot ${state.toLowerCase()}`} />{state}
            </button>
          ))}
        </div>
      </fieldset>
    </FilterSection>
  );
}

function LayerOption({
  checked,
  iconPath,
  title,
  meta,
  onChange,
}: {
  checked: boolean;
  iconPath: string;
  title: string;
  meta: string;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      className={`layer-option ${checked ? 'selected' : ''}`}
      aria-pressed={checked}
      onClick={onChange}
    >
      <img src={iconPath} alt="" aria-hidden="true" />
      <span><strong>{title}</strong><small>{meta}</small></span>
      <span className="check-box">{checked && <Check size={13} strokeWidth={3} />}</span>
    </button>
  );
}

function WorldSidebar({
  collectibleLayers,
  selectedCollectibleKinds,
  setSelectedCollectibleKinds,
  players,
  hiddenPlayerIds,
  setHiddenPlayerIds,
  mapMarkerCount,
  showMapMarkers,
  setShowMapMarkers,
  ...resourceProps
}: React.ComponentProps<typeof ResourceFilters> & {
  collectibleLayers: CollectibleLayerStatus[];
  selectedCollectibleKinds: Set<CollectibleKind>;
  setSelectedCollectibleKinds: React.Dispatch<React.SetStateAction<Set<CollectibleKind>>>;
  players: PlayerSnapshot[];
  hiddenPlayerIds: Set<string>;
  setHiddenPlayerIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  mapMarkerCount: number;
  showMapMarkers: boolean;
  setShowMapMarkers: (value: boolean) => void;
}) {
  const toggleSet = <T,>(setter: React.Dispatch<React.SetStateAction<Set<T>>>, item: T) => {
    setter((current) => {
      const next = new Set(current);
      if (next.has(item)) next.delete(item); else next.add(item);
      return next;
    });
  };

  return (
    <>
      <div className="sidebar-summary">
        <span className="eyebrow">Kartenebenen</span>
        <h2>Was möchtest du sehen?</h2>
        <p>Alle Einträge stammen aus dem aktuell geladenen Savegame.</p>
      </div>
      <ResourceFilters {...resourceProps} />
      <FilterSection title="Sammelobjekte" icon={<Layers3 size={17} />} count={selectedCollectibleKinds.size} defaultOpen={false}>
        <div className="layer-option-list">
          {collectibleLayers.length > 0 ? collectibleLayers.map((layer) => (
            <LayerOption
              key={layer.kind}
              checked={selectedCollectibleKinds.has(layer.kind)}
              iconPath={layer.iconPath}
              title={layer.pluralLabel}
              meta={`${layer.remainingCount.toLocaleString('de-DE')} offen · ${layer.collected.toLocaleString('de-DE')} gesammelt`}
              onChange={() => toggleSet(setSelectedCollectibleKinds, layer.kind)}
            />
          )) : <div className="empty-inline">Keine Sammelobjektdaten im geladenen Save verfügbar.</div>}
        </div>
      </FilterSection>
      <FilterSection title="Spieler & Marker" icon={<Users size={17} />} count={players.length + mapMarkerCount} defaultOpen={false}>
        <div className="layer-option-list">
          {players.map((player) => (
            <LayerOption
              key={player.id}
              checked={!hiddenPlayerIds.has(player.id)}
              iconPath="/icons/player.svg"
              title={player.name}
              meta="Spielerposition aus dem Savegame"
              onChange={() => toggleSet(setHiddenPlayerIds, player.id)}
            />
          ))}
          {mapMarkerCount > 0 && (
            <LayerOption
              checked={showMapMarkers}
              iconPath="/icons/map-marker.svg"
              title="Kartenmarker & Stempel"
              meta={`${mapMarkerCount.toLocaleString('de-DE')} Einträge aus dem Savegame`}
              onChange={() => setShowMapMarkers(!showMapMarkers)}
            />
          )}
          {players.length === 0 && mapMarkerCount === 0 && <div className="empty-inline">Keine Spieler oder Kartenmarker im geladenen Save verfügbar.</div>}
        </div>
      </FilterSection>
    </>
  );
}

function RailSidebar({
  showTracks,
  showSignals,
  showStations,
  showTrains,
  setShowTracks,
  setShowSignals,
  setShowStations,
  setShowTrains,
  onSelectStation,
  onSelectTrain,
  selectedTrainId,
  metrics,
  stations,
  trains,
}: {
  showTracks: boolean;
  showSignals: boolean;
  showStations: boolean;
  showTrains: boolean;
  setShowTracks: (value: boolean) => void;
  setShowSignals: (value: boolean) => void;
  setShowStations: (value: boolean) => void;
  setShowTrains: (value: boolean) => void;
  onSelectStation: (station: PositionedRailStation) => void;
  onSelectTrain: (train: PositionedRailTrain) => void;
  selectedTrainId: string | null;
  metrics: { tracks: number; signals: number; stations: number; trains: number; wagons: number } | null;
  stations: PositionedRailStation[];
  trains: PositionedRailTrain[];
}) {
  return (
    <>
      <div className="sidebar-summary rail-summary">
        <span className="eyebrow">Savegame-Daten</span>
        <h2>Zugnetz</h2>
        <p>{metrics ? 'Gleisverläufe, Signale, Züge und Fahrpläne aus dem aktuellen Savegame.' : 'Im geladenen Datensatz sind keine Zugdaten verfügbar.'}</p>
        <div className="network-kpis">
          <div><strong>{metrics?.tracks.toLocaleString('de-DE') ?? '–'}</strong><span>Schienenobjekte</span></div>
          <div><strong>{metrics?.stations.toLocaleString('de-DE') ?? '–'}</strong><span>Bahnhöfe</span></div>
          <div><strong>{metrics?.trains.toLocaleString('de-DE') ?? '–'}</strong><span>Züge</span></div>
        </div>
      </div>
      <FilterSection title="Sichtbare Daten" icon={<Layers3 size={17} />}>
        <div className="setting-row"><span><TrainTrack size={17} /> Schienennetz</span><Toggle checked={showTracks} onChange={() => setShowTracks(!showTracks)} label="Schienennetz anzeigen" /></div>
        <div className="setting-row"><span><span className="signal-key" /> Signale ({metrics?.signals ?? 0})</span><Toggle checked={showSignals} onChange={() => setShowSignals(!showSignals)} label="Signale anzeigen" /></div>
        <div className="setting-row"><span><img className="setting-icon" src="/icons/train-station.svg" alt="" /> Bahnhöfe</span><Toggle checked={showStations} onChange={() => setShowStations(!showStations)} label="Bahnhöfe anzeigen" /></div>
        <div className="setting-row"><span><img className="setting-icon" src="/icons/vehicle.svg" alt="" /> Züge</span><Toggle checked={showTrains} onChange={() => setShowTrains(!showTrains)} label="Züge anzeigen" /></div>
      </FilterSection>
      <div className="data-boundary-note"><CircleHelp size={16} /><p><strong>Savegame-Snapshot</strong>Routen werden aus Fahrplan und verbundenem Gleisgraph berechnet. Historische Auslastung und ETA-Zeitreihen enthält das Savegame nicht.</p></div>
      <FilterSection title="Züge & Linien" icon={<TrainFront size={17} />} count={trains.length}>
        <div className="train-list">
          {trains.map((train) => (
            <button type="button" className={`train-list-item ${selectedTrainId === train.id ? 'selected' : ''}`} key={train.id} onClick={() => onSelectTrain(train)}>
              <span className="route-stripe" style={{ backgroundColor: train.color }} />
              <span className="train-copy"><strong>{train.name}</strong><small>{train.stops.length} Stopps · {train.routeTrackIds.length} Gleisabschnitte</small></span>
              <span className={`train-state ${train.selfDriving ? '' : 'waiting'}`}>{train.selfDriving ? 'Auto' : 'Manuell'}</span>
            </button>
          ))}
        </div>
      </FilterSection>
      <FilterSection title="Bahnhöfe" icon={<TrainTrack size={17} />} count={stations.length} defaultOpen={false}>
        <div className="train-list">
          {stations.map((station) => (
            <button type="button" className="train-list-item" key={station.id} onClick={() => onSelectStation(station)}>
              <img className="rail-list-icon" src="/icons/train-station.svg" alt="" />
              <span className="train-copy"><strong>{station.name}</strong><small>{stationPlatformSummary(station)}</small></span>
              <LocateFixed size={15} />
            </button>
          ))}
        </div>
      </FilterSection>
    </>
  );
}

function dockingRuleLabel(rule: RailTrainSnapshot['stops'][number]['rule']): string {
  const action = rule.definition === 'fully' ? 'Vollständig be-/entladen' : rule.definition === 'once' ? 'Ein Ladevorgang' : 'Dockingregel';
  return rule.dockForSeconds !== null ? `${action} · ${rule.dockForSeconds.toLocaleString('de-DE')} s` : action;
}

function stationPlatformSummary(station: RailStationSnapshot): string {
  const counts = station.platforms.reduce((result, platform) => ({
    ...result,
    [platform.kind]: result[platform.kind] + 1,
  }), { freight: 0, fluid: 0, empty: 0 });
  const parts = [
    counts.freight > 0 ? `${counts.freight} Fracht` : null,
    counts.fluid > 0 ? `${counts.fluid} Flüssigkeit` : null,
    counts.empty > 0 ? `${counts.empty} Leer` : null,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : 'Keine Plattformen';
}

function platformKindLabel(kind: RailStationSnapshot['platforms'][number]['kind']): string {
  if (kind === 'fluid') return 'Flüssig';
  if (kind === 'empty') return 'Leer';
  return 'Fracht';
}

function RailDetail({ selection, trains, onClose }: { selection: RailSelection; trains: PositionedRailTrain[]; onClose: () => void }) {
  const isStation = selection.kind === 'station';
  const station = isStation ? selection.station : null;
  const train = !isStation ? selection.train : null;
  const title = station?.name ?? train?.name ?? 'Zugnetz';
  const stationTrains = station ? trains.filter((item) => item.stops.some((stop) => stop.stationIdentifierId === station.identifierId)) : [];
  return (
    <aside className="detail-panel" aria-label={`${isStation ? 'Bahnhof' : 'Zug'} ${title}`}>
      <div className={`detail-hero ${train ? 'train-hero' : ''}`} style={train ? { '--route': train.color } as React.CSSProperties : undefined}>
        <button className="icon-button detail-close" type="button" onClick={onClose} aria-label="Detailansicht schließen"><X size={18} /></button>
        <div className="station-monogram"><img src={isStation ? '/icons/train-station.svg' : '/icons/vehicle.svg'} alt="" aria-hidden="true" /></div>
        <span className="eyebrow">{isStation ? 'Bahnhof' : 'Zug'} · Savegame</span>
        <h2>{title}</h2>
        <div className={`detail-status ${train && !train.selfDriving ? 'warning' : ''}`}><span />{train ? (train.selfDriving ? 'Selbstfahrend' : 'Manueller Betrieb') : `${stationTrains.length} Linien halten hier`}</div>
      </div>
      <div className="detail-scroll">
        <div className="metric-strip">
          <div><MapPin size={16} /><strong>{Math.round((station ?? train)!.x).toLocaleString('de-DE')}</strong><span>X</span></div>
          <div><MapPin size={16} /><strong>{Math.round((station ?? train)!.y).toLocaleString('de-DE')}</strong><span>Y</span></div>
          <div><MapPin size={16} /><strong>{Math.round((station ?? train)!.z).toLocaleString('de-DE')}</strong><span>Z</span></div>
        </div>
        {station && <>
          <section className="detail-section"><div className="detail-title"><h3>Linien an diesem Bahnhof</h3><span>{stationTrains.length}</span></div>
            <div className="detail-route-list">{stationTrains.map((item) => <div key={item.id}><i style={{ backgroundColor: item.color }} /><span><strong>{item.name}</strong><small>{item.stops.map((stop) => stop.stationName).join(' → ')}</small></span></div>)}</div>
          </section>
          <section className="detail-section"><div className="detail-title"><h3>Plattformen</h3><span>{station.platforms.length}</span></div>
            <div className="platform-list">{station.platforms.length ? station.platforms.map((platform, index) => {
              const rate = platform.mode === 'load' ? platform.loadRate : platform.mode === 'unload' ? platform.unloadRate : null;
              return <div className="platform-row" key={platform.id}><span className={`platform-kind ${platform.kind}`}>{platformKindLabel(platform.kind)}</span><span><strong>Position {index + 1}</strong><small>{platform.mode === 'load' ? 'Laden' : platform.mode === 'unload' ? 'Entladen' : 'Abstand ohne Umschlag'}</small></span><strong>{rate === null ? '—' : `${rate.toLocaleString('de-DE', { maximumFractionDigits: 3 })}/s`}</strong></div>;
            }) : <p className="empty-inline">Keine Plattform mit diesem Bahnhof verbunden.</p>}</div>
          </section>
          {station.currentDockDurationSeconds !== null && <div className="data-boundary-note detail-boundary"><CircleHelp size={16} /><p><strong>Aktueller Dockvorgang</strong>{station.currentDockDurationSeconds.toLocaleString('de-DE', { maximumFractionDigits: 1 })} Sekunden im Save-Snapshot.</p></div>}
        </>}
        {train && <section className="detail-section"><div className="detail-title"><h3>Fahrplan</h3><span>{train.stops.length} Stopps</span></div>
          <ol className="schedule-list">{train.stops.map((stop, index) => <li className={index === train.currentStopIndex ? 'current' : ''} key={`${stop.stationIdentifierId}:${index}`}><span>{index + 1}</span><div><strong>{stop.stationName}</strong><small>{dockingRuleLabel(stop.rule)}</small>{(stop.rule.loadFilters.length > 0 || stop.rule.unloadFilters.length > 0) && <em>{stop.rule.loadFilters.length ? `Laden: ${stop.rule.loadFilters.join(', ')}` : ''}{stop.rule.loadFilters.length && stop.rule.unloadFilters.length ? ' · ' : ''}{stop.rule.unloadFilters.length ? `Entladen: ${stop.rule.unloadFilters.join(', ')}` : ''}</em>}</div></li>)}</ol>
          <div className="data-boundary-note detail-boundary"><CircleHelp size={16} /><p><strong>Berechnete Linie</strong>{train.routeTrackIds.length} verbundene Gleisabschnitte zwischen den Fahrplan-Stopps.</p></div>
        </section>}
      </div>
    </aside>
  );
}

function MapCanvas({
  viewMode,
  filteredResources,
  collectibles,
  players,
  mapMarkers,
  railTracks,
  railSignals,
  stations,
  trains,
  showTracks,
  showSignals,
  showStations,
  showTrains,
  panelOpen,
  selectedDetail,
  onSelectStation,
  onSelectTrain,
  imageUrl,
}: {
  viewMode: ViewMode;
  filteredResources: ResourceNode[];
  collectibles: PositionedCollectible[];
  players: PositionedPlayer[];
  mapMarkers: PositionedMapMarker[];
  railTracks: PositionedRailTrack[];
  railSignals: PositionedRailSignal[];
  stations: PositionedRailStation[];
  trains: PositionedRailTrain[];
  showTracks: boolean;
  showSignals: boolean;
  showStations: boolean;
  showTrains: boolean;
  panelOpen: boolean;
  selectedDetail: RailSelection | null;
  onSelectStation: (station: PositionedRailStation) => void;
  onSelectTrain: (train: PositionedRailTrain) => void;
  imageUrl: string;
}) {
  const selectedPosition = selectedDetail?.kind === 'station' ? selectedDetail.station : selectedDetail?.train;
  const focusTarget = selectedPosition ? { x: selectedPosition.mapX, y: selectedPosition.mapY } : null;
  const trackById = useMemo(() => new Map(railTracks.map((track) => [track.id, track])), [railTracks]);
  const railRouteLayers = useMemo(() => trains.flatMap((train) => {
    const positions = train.routeTrackIds.flatMap((id) => {
      const track = trackById.get(id);
      return track ? [track.positions] : [];
    });
    return positions.length > 0 ? [{ train, positions }] : [];
  }), [trackById, trains]);
  const selectedTrainId = selectedDetail?.kind === 'train' ? selectedDetail.train.id : null;
  const regularRouteLayers = railRouteLayers.filter(({ train }) => train.id !== selectedTrainId);
  const selectedRouteLayer = railRouteLayers.find(({ train }) => train.id === selectedTrainId);
  return (
    <MapContainer
      className={`map-canvas ${viewMode === 'rail' ? 'rail-mode' : 'world-mode'}`}
      crs={L.CRS.Simple}
      bounds={MAP_BOUNDS}
      minZoom={-2.35}
      maxZoom={2.5}
      zoomSnap={0.25}
      zoomAnimation={false}
      markerZoomAnimation={false}
      fadeAnimation={false}
      wheelDebounceTime={35}
      wheelPxPerZoomLevel={95}
      attributionControl={false}
      maxBounds={[[-350, -350], [5350, 5350]]}
    >
      <MapResize panelOpen={panelOpen} detailOpen={Boolean(selectedDetail)} />
      <FocusMap target={focusTarget} />
      <MapModeController viewMode={viewMode} hasTarget={Boolean(selectedDetail)} />
      <ImageOverlay url={imageUrl} bounds={MAP_BOUNDS} className="world-image" opacity={viewMode === 'rail' ? 0.58 : 1} />
      {viewMode === 'world' && filteredResources.map((node) => (
        <Marker
          key={node.id}
          position={toLatLng(node.x, node.y)}
          icon={L.icon({
            iconUrl: RESOURCE_ICON_PATH[node.kind],
            iconSize: [30, 30],
            iconAnchor: [15, 15],
            tooltipAnchor: [0, -14],
            className: `resource-map-icon purity-${node.purity.toLowerCase()} ${node.state === 'Belegt' ? 'is-used' : ''}`,
          })}
        >
          <Tooltip direction="top" offset={[0, -8]} opacity={1}>
            <div className="map-tooltip"><strong>{node.kind}-Erzquelle</strong><span>{node.purity} · {node.state}</span></div>
          </Tooltip>
        </Marker>
      ))}
      {viewMode === 'world' && collectibles.map((collectible) => (
        <Marker
          key={collectible.id}
          position={toLatLng(collectible.x, collectible.y)}
          icon={L.icon({ iconUrl: collectible.iconPath, iconSize: [27, 27], iconAnchor: [13.5, 13.5], className: 'collectible-map-icon' })}
        >
          <Tooltip direction="top" offset={[0, -10]} opacity={1}>
            <div className="map-tooltip"><strong>{collectible.label}</strong><span>Noch nicht gesammelt · Z {Math.round(collectible.z).toLocaleString('de-DE')}</span></div>
          </Tooltip>
        </Marker>
      ))}
      {viewMode === 'world' && players.map((player) => (
        <Marker
          key={player.id}
          position={toLatLng(player.mapX, player.mapY)}
          icon={L.icon({ iconUrl: '/icons/player.svg', iconSize: [30, 30], iconAnchor: [15, 15], className: 'player-map-icon' })}
        >
          <Tooltip direction="top" offset={[0, -11]} opacity={1}>
            <div className="map-tooltip"><strong>{player.name}</strong><span>Spielerposition · Z {Math.round(player.z).toLocaleString('de-DE')}</span></div>
          </Tooltip>
        </Marker>
      ))}
      {viewMode === 'world' && mapMarkers.map((marker) => (
        <Marker
          key={marker.id}
          position={toLatLng(marker.mapX, marker.mapY)}
          icon={L.icon({ iconUrl: '/icons/map-marker.svg', iconSize: [27, 27], iconAnchor: [13.5, 26], className: 'save-marker-map-icon' })}
        >
          <Tooltip direction="top" offset={[0, -23]} opacity={1}>
            <div className="map-tooltip"><strong>{marker.name}</strong><span>{marker.categoryName ?? (marker.markerType === 'stamp' ? 'Stempel' : 'Kartenmarker')}</span></div>
          </Tooltip>
        </Marker>
      ))}
      {viewMode === 'rail' && showTracks && railTracks.length > 0 && (<>
        <Polyline positions={railTracks.map((track) => track.positions)} pathOptions={{ color: '#0e120f', weight: 4.6, opacity: 0.86, lineCap: 'round', lineJoin: 'round' }} interactive={false} />
        <Polyline positions={railTracks.map((track) => track.positions)} pathOptions={{ color: '#a8b0a9', weight: 1.8, opacity: 0.88, lineCap: 'round', lineJoin: 'round' }} interactive={false} />
      </>)}
      {viewMode === 'rail' && showTracks && regularRouteLayers.map(({ train, positions }) => (
        <Polyline key={`route-case:${train.id}`} positions={positions} pathOptions={{ color: '#0b0e0c', weight: selectedRouteLayer ? 5.2 : 5.6, opacity: selectedRouteLayer ? 0.72 : 0.82, lineCap: 'round', lineJoin: 'round' }} interactive={false} />
      ))}
      {viewMode === 'rail' && showTracks && regularRouteLayers.map(({ train, positions }) => (
        <Polyline key={`route:${train.id}`} positions={positions} pathOptions={{ color: selectedRouteLayer ? '#858c87' : train.color, weight: selectedRouteLayer ? 2.5 : 3, opacity: selectedRouteLayer ? 0.62 : 0.9, lineCap: 'round', lineJoin: 'round' }} interactive={false} />
      ))}
      {viewMode === 'rail' && showTracks && selectedRouteLayer && (<>
        <Polyline positions={selectedRouteLayer.positions} pathOptions={{ color: '#080b09', weight: 7.4, opacity: 0.96, lineCap: 'round', lineJoin: 'round' }} interactive={false} />
        <Polyline positions={selectedRouteLayer.positions} pathOptions={{ color: selectedRouteLayer.train.color, weight: 4.4, opacity: 1, lineCap: 'round', lineJoin: 'round' }} interactive={false} />
      </>)}
      {viewMode === 'rail' && showSignals && railSignals.map((signal) => (
        <CircleMarker key={signal.id} center={toLatLng(signal.mapX, signal.mapY)} radius={signal.type === 'path' ? 3.2 : 2.6} pathOptions={{ color: signal.type === 'path' ? '#6bbbe9' : '#e9ad35', fillColor: '#171916', fillOpacity: 1, weight: 1.4 }}>
          <Tooltip direction="top" opacity={1}><div className="map-tooltip"><strong>{signal.type === 'path' ? 'Pfadsignal' : 'Blocksignal'}</strong><span>{signal.guardedTrackIds.length} bewachte · {signal.observedTrackIds.length} beobachtete Gleise</span></div></Tooltip>
        </CircleMarker>
      ))}
      {viewMode === 'rail' && showStations && stations.map((station) => (
        <Marker
          key={station.id}
          position={toLatLng(station.mapX, station.mapY)}
          eventHandlers={{ click: () => onSelectStation(station) }}
          icon={L.icon({ iconUrl: '/icons/train-station.svg', iconSize: [34, 34], iconAnchor: [17, 17], className: 'rail-station-map-icon' })}
        >
          <Tooltip direction="top" offset={[0, -14]} opacity={1}>
            <div className="map-tooltip"><strong>{station.name}</strong><span>{stationPlatformSummary(station)} · {trains.filter((train) => train.stops.some((stop) => stop.stationIdentifierId === station.identifierId)).length} Linien</span></div>
          </Tooltip>
        </Marker>
      ))}
      {viewMode === 'rail' && showTrains && trains.map((train) => (
        <Marker
          key={train.id}
          position={toLatLng(train.mapX, train.mapY)}
          eventHandlers={{ click: () => onSelectTrain(train) }}
          icon={L.icon({ iconUrl: '/icons/vehicle.svg', iconSize: [30, 30], iconAnchor: [15, 15], className: 'locomotive-map-icon' })}
        >
          <Tooltip direction="top" offset={[0, -12]} opacity={1}>
            <div className="map-tooltip"><strong>{train.name}</strong><span>{train.stops.length} Fahrplan-Stopps · {train.selfDriving ? 'Selbstfahrend' : 'Manuell'}</span></div>
          </Tooltip>
        </Marker>
      ))}
    </MapContainer>
  );
}

function SatisMapApplication() {
  const {
    status,
    mapData,
    mapConfiguration,
    analysisData,
    error: dataError,
    loading: dataLoading,
    refreshing: dataRefreshing,
    reload,
  } = useSatisMapData();
  const [viewMode, setViewMode] = useState<ViewMode>('world');
  const [panelOpen, setPanelOpen] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedKinds, setSelectedKinds] = useState<Set<ResourceKind>>(new Set(['Eisen']));
  const [puritiesByKind, setPuritiesByKind] = useState<PurityByResource>(allResourcePurities);
  const [selectedStates, setSelectedStates] = useState<Set<ResourceState>>(new Set(STATES));
  const [selectedCollectibleKinds, setSelectedCollectibleKinds] = useState<Set<CollectibleKind>>(new Set());
  const [hiddenPlayerIds, setHiddenPlayerIds] = useState<Set<string>>(new Set());
  const [showMapMarkers, setShowMapMarkers] = useState(true);
  const [showTracks, setShowTracks] = useState(true);
  const [showSignals, setShowSignals] = useState(false);
  const [showStations, setShowStations] = useState(true);
  const [showTrains, setShowTrains] = useState(true);
  const [selectedDetail, setSelectedDetail] = useState<RailSelection | null>(null);

  const liveResources = useMemo(() => {
    if (!mapData || !mapConfiguration) return [];
    return mapData.resourceLayers.flatMap((layer) => layer.nodes.flatMap((node) => {
      if (!node.resourceType) return [];
      const pixel = worldToMapPixelPosition(
        node.x,
        node.y,
        mapConfiguration.worldBounds,
        mapConfiguration.imageWidth,
        mapConfiguration.imageHeight,
        mapConfiguration.projection,
      );
      const purity = node.purity ? API_PURITY_TO_LABEL[node.purity] : 'Normal';
      return [{
        id: node.id,
        x: (pixel.x / mapConfiguration.imageWidth) * MAP_SIZE,
        y: (pixel.y / mapConfiguration.imageHeight) * MAP_SIZE,
        kind: RESOURCE_TYPE_TO_KIND[node.resourceType],
        purity,
        state: API_USAGE_TO_LABEL[node.usage],
        rate: purity === 'Rein' ? 240 : purity === 'Normal' ? 120 : 60,
      }];
    }));
  }, [mapConfiguration, mapData]);

  const availableResources = liveResources;
  const visibleCollectibles = useMemo<PositionedCollectible[]>(() => {
    if (!mapData || !mapConfiguration) return [];
    return mapData.collectibleLayers.flatMap((layer) => {
      if (!selectedCollectibleKinds.has(layer.kind)) return [];
      return layer.remaining.map((item) => {
        const pixel = worldToMapPixelPosition(
          item.x,
          item.y,
          mapConfiguration.worldBounds,
          mapConfiguration.imageWidth,
          mapConfiguration.imageHeight,
          mapConfiguration.projection,
        );
        return {
          id: item.id,
          kind: item.kind,
          label: layer.label,
          iconPath: layer.iconPath,
          x: (pixel.x / mapConfiguration.imageWidth) * MAP_SIZE,
          y: (pixel.y / mapConfiguration.imageHeight) * MAP_SIZE,
          z: item.z,
        };
      });
    });
  }, [mapConfiguration, mapData, selectedCollectibleKinds]);

  const visiblePlayers = useMemo<PositionedPlayer[]>(() => {
    if (!mapData || !mapConfiguration) return [];
    return mapData.players.flatMap((player) => {
      if (hiddenPlayerIds.has(player.id)) return [];
      const pixel = worldToMapPixelPosition(
        player.x,
        player.y,
        mapConfiguration.worldBounds,
        mapConfiguration.imageWidth,
        mapConfiguration.imageHeight,
        mapConfiguration.projection,
      );
      return [{
        ...player,
        mapX: (pixel.x / mapConfiguration.imageWidth) * MAP_SIZE,
        mapY: (pixel.y / mapConfiguration.imageHeight) * MAP_SIZE,
      }];
    });
  }, [hiddenPlayerIds, mapConfiguration, mapData]);

  const visibleMapMarkers = useMemo<PositionedMapMarker[]>(() => {
    if (!showMapMarkers || !mapData || !mapConfiguration) return [];
    return mapData.mapMarkers.map((marker) => {
      const pixel = worldToMapPixelPosition(
        marker.x,
        marker.y,
        mapConfiguration.worldBounds,
        mapConfiguration.imageWidth,
        mapConfiguration.imageHeight,
        mapConfiguration.projection,
      );
      return {
        ...marker,
        mapX: (pixel.x / mapConfiguration.imageWidth) * MAP_SIZE,
        mapY: (pixel.y / mapConfiguration.imageHeight) * MAP_SIZE,
      };
    });
  }, [mapConfiguration, mapData, showMapMarkers]);

  const railNetwork = useMemo(() => {
    const empty = { tracks: [] as PositionedRailTrack[], signals: [] as PositionedRailSignal[], stations: [] as PositionedRailStation[], trains: [] as PositionedRailTrain[] };
    if (!analysisData?.analysis.available || !mapConfiguration) return empty;
    const project = (x: number, y: number) => {
      const pixel = worldToMapPixelPosition(
        x,
        y,
        mapConfiguration.worldBounds,
        mapConfiguration.imageWidth,
        mapConfiguration.imageHeight,
        mapConfiguration.projection,
      );
      return {
        mapX: (pixel.x / mapConfiguration.imageWidth) * MAP_SIZE,
        mapY: (pixel.y / mapConfiguration.imageHeight) * MAP_SIZE,
      };
    };
    const logistics = analysisData.analysis.logistics;
    return {
      tracks: logistics.railTracks.map((track) => ({
        ...track,
        positions: track.points.map((point) => {
          const projected = project(point.x, point.y);
          return toLatLng(projected.mapX, projected.mapY);
        }),
      })),
      signals: logistics.railSignals.map((signal) => ({ ...signal, ...project(signal.x, signal.y) })),
      stations: logistics.railStations.map((station) => ({ ...station, ...project(station.x, station.y) })),
      trains: logistics.railTrains.map((train, index) => ({ ...train, ...project(train.x, train.y), color: ROUTE_COLORS[index % ROUTE_COLORS.length]! })),
    };
  }, [analysisData, mapConfiguration]);

  const { tracks: railTracks, signals: railSignals, stations, trains } = railNetwork;
  const railMetrics = analysisData?.analysis.available ? {
    tracks: analysisData.analysis.logistics.railwayTracks,
    signals: analysisData.analysis.logistics.railSignals.length,
    stations: analysisData.analysis.logistics.trainStations,
    trains: analysisData.analysis.logistics.trains,
    wagons: analysisData.analysis.logistics.freightWagons,
  } : null;
  const activeResourceCounts = useMemo(() => Object.fromEntries(
    RESOURCE_KINDS.map((kind) => [kind, availableResources.filter((node) => node.kind === kind).length]),
  ) as Record<ResourceKind, number>, [availableResources]);

  const filteredResources = useMemo(() => availableResources.filter((node) => (
    selectedKinds.has(node.kind)
    && puritiesByKind[node.kind].has(node.purity)
    && selectedStates.has(node.state)
  )), [availableResources, puritiesByKind, selectedKinds, selectedStates]);

  const searchResults = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return [];
    return [
      ...stations.map((station) => ({ id: station.id, label: station.name, kind: 'station' as const, selection: { kind: 'station' as const, station } })),
      ...trains.map((train) => ({ id: train.id, label: train.name, kind: 'train' as const, selection: { kind: 'train' as const, train } })),
    ]
      .filter((item) => item.label.toLowerCase().includes(query))
      .slice(0, 8);
  }, [search, stations, trains]);

  const changeMode = (mode: ViewMode) => {
    setViewMode(mode);
    setSelectedDetail(null);
    setSearch('');
  };

  const refresh = () => void reload();
  const lastUpdated = mapData ? new Date(mapData.generatedAt) : null;
  const dataLabel = mapData ? 'Live-Save' : dataLoading ? 'Verbinden …' : 'Keine Daten';

  const selectSearchResult = (item: (typeof searchResults)[number]) => {
    setViewMode('rail');
    setSelectedDetail(item.selection);
    setSearch('');
    setSearchOpen(false);
  };

  const filters = {
    selectedKinds,
    puritiesByKind,
    selectedStates,
    setSelectedKinds,
    setPuritiesByKind,
    setSelectedStates,
    counts: activeResourceCounts,
    collectibleLayers: mapData?.collectibleLayers ?? [],
    selectedCollectibleKinds,
    setSelectedCollectibleKinds,
    players: mapData?.players ?? [],
    hiddenPlayerIds,
    setHiddenPlayerIds,
    mapMarkerCount: mapData?.mapMarkers.length ?? 0,
    showMapMarkers,
    setShowMapMarkers,
  };

  const visibleWorldCount = filteredResources.length + visibleCollectibles.length + visiblePlayers.length + visibleMapMarkers.length;

  return (
    <div className={`app-shell ${panelOpen ? 'panel-open' : ''} ${selectedDetail ? 'detail-open' : ''}`}>
      <header className="topbar">
        <button className="brand" type="button" onClick={() => changeMode('world')} aria-label="SatisMap Startseite">
          <span className="brand-mark"><MapIcon size={19} strokeWidth={2.5} /></span>
          <span><strong>SatisMap</strong><small>Savegame-Karte</small></span>
        </button>
        <nav className="mode-switch" aria-label="Hauptansichten">
          <button type="button" className={viewMode === 'world' ? 'active' : ''} onClick={() => changeMode('world')}><MapIcon size={17} />Weltkarte</button>
          <button type="button" className={viewMode === 'rail' ? 'active' : ''} onClick={() => changeMode('rail')}><TrainFront size={17} />Zugnetz</button>
        </nav>
        <div className="topbar-actions">
          <div className={`global-search ${searchOpen ? 'is-open' : ''}`}>
            <Search size={17} />
            <input
              type="search"
              aria-label="Karte durchsuchen"
              placeholder="Bahnhof oder Zug suchen …"
              value={search}
              onFocus={() => setSearchOpen(true)}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && <button type="button" onClick={() => setSearch('')} aria-label="Suche leeren"><X size={15} /></button>}
            {searchOpen && search && (
              <div className="search-results">
                {searchResults.length ? searchResults.map((item) => (
                  <button type="button" key={item.id} onClick={() => selectSearchResult(item)}>
                    <span className="search-result-icon">{item.kind === 'station' ? <TrainTrack size={16} /> : <TrainFront size={16} />}</span>
                    <span><strong>{item.label}</strong><small>{item.kind === 'station' ? 'Bahnhof' : 'Zug'}</small></span>
                    <LocateFixed size={15} />
                  </button>
                )) : <div className="search-empty">Keine passenden Züge oder Bahnhöfe</div>}
              </div>
            )}
          </div>
          <button className="icon-button mobile-search-button" type="button" onClick={() => setSearchOpen(!searchOpen)} aria-label="Suche"><Search size={18} /></button>
          <div className="sync-state" title={dataError ?? status?.sessionName ?? undefined}><span className={`sync-dot ${mapData ? '' : 'demo'}`} /><span><strong>{dataLabel}</strong><small>{lastUpdated ? `${lastUpdated.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr` : 'nicht geladen'}</small></span></div>
          <button className="icon-button" type="button" onClick={refresh} aria-label="Daten aktualisieren"><RefreshCw size={18} className={dataRefreshing ? 'spinning' : ''} /></button>
          <button className="icon-button mobile-menu-button" type="button" onClick={() => setMobileFiltersOpen(true)} aria-label="Filter öffnen"><Menu size={20} /></button>
        </div>
      </header>

      <aside className={`sidebar ${panelOpen ? '' : 'collapsed'}`}>
        <button className="collapse-button" type="button" onClick={() => setPanelOpen(!panelOpen)} aria-label={panelOpen ? 'Filterleiste schließen' : 'Filterleiste öffnen'}>
          {panelOpen ? <PanelLeftClose size={17} /> : <SlidersHorizontal size={17} />}
        </button>
        <div className="sidebar-scroll">
          {viewMode === 'world' ? <WorldSidebar {...filters} /> : (
            <RailSidebar
              showTracks={showTracks}
              showSignals={showSignals}
              showStations={showStations}
              showTrains={showTrains}
              setShowTracks={setShowTracks}
              setShowSignals={setShowSignals}
              setShowStations={setShowStations}
              setShowTrains={setShowTrains}
              onSelectStation={(station) => setSelectedDetail({ kind: 'station', station })}
              onSelectTrain={(train) => setSelectedDetail({ kind: 'train', train })}
              selectedTrainId={selectedDetail?.kind === 'train' ? selectedDetail.train.id : null}
              metrics={railMetrics}
              stations={stations}
              trains={trains}
            />
          )}
        </div>
      </aside>

      <main className="map-region">
        <MapCanvas
          viewMode={viewMode}
          filteredResources={filteredResources}
          collectibles={visibleCollectibles}
          players={visiblePlayers}
          mapMarkers={visibleMapMarkers}
          railTracks={railTracks}
          railSignals={railSignals}
          stations={stations}
          trains={trains}
          showTracks={showTracks}
          showSignals={showSignals}
          showStations={showStations}
          showTrains={showTrains}
          panelOpen={panelOpen}
          selectedDetail={selectedDetail}
          onSelectStation={(station) => setSelectedDetail({ kind: 'station', station })}
          onSelectTrain={(train) => setSelectedDetail({ kind: 'train', train })}
          imageUrl={mapConfiguration?.imagePath ?? '/map/satisfactory-map.webp'}
        />
        <div className="map-vignette" />
        <div className="map-status-bar">
          <span><LocateFixed size={14} /> {status?.sessionName ?? status?.saveName ?? 'Satisfactory-Welt'}</span>
          <span>{viewMode === 'rail' ? `${railMetrics?.tracks.toLocaleString('de-DE') ?? 0} Gleisabschnitte · ${railSignals.length} Signale · ${trains.length} Züge` : `${visibleWorldCount.toLocaleString('de-DE')} Kartenobjekte sichtbar`}</span>
        </div>
        {!dataLoading && !mapData && (
          <div className="map-empty-state">
            <span><CircleHelp size={23} /></span>
            <h3>Keine Savegame-Daten geladen</h3>
            <p>{dataError ?? 'Das Backend hat noch keinen gültigen Weltstand geliefert.'}</p>
            <button type="button" onClick={refresh}>Erneut laden</button>
          </div>
        )}
        <button className="mobile-filter-fab" type="button" onClick={() => setMobileFiltersOpen(true)}><SlidersHorizontal size={18} />Filter</button>
      </main>

      {selectedDetail && <RailDetail selection={selectedDetail} trains={trains} onClose={() => setSelectedDetail(null)} />}

      {mobileFiltersOpen && (
        <div className="mobile-sheet-layer" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) setMobileFiltersOpen(false); }}>
          <aside className="mobile-sheet" aria-label="Kartenfilter">
            <div className="sheet-handle" />
            <div className="sheet-header"><div><span className="eyebrow">{viewMode === 'world' ? 'Kartenebenen' : 'Zugnetz'}</span><h2>Darstellung</h2></div><button className="icon-button" type="button" onClick={() => setMobileFiltersOpen(false)} aria-label="Filter schließen"><X size={18} /></button></div>
            <div className="sheet-content">
              {viewMode === 'world' ? <WorldSidebar {...filters} /> : (
                <RailSidebar showTracks={showTracks} showSignals={showSignals} showStations={showStations} showTrains={showTrains} setShowTracks={setShowTracks} setShowSignals={setShowSignals} setShowStations={setShowStations} setShowTrains={setShowTrains} onSelectStation={(station) => { setSelectedDetail({ kind: 'station', station }); setMobileFiltersOpen(false); }} onSelectTrain={(train) => { setSelectedDetail({ kind: 'train', train }); setMobileFiltersOpen(false); }} selectedTrainId={selectedDetail?.kind === 'train' ? selectedDetail.train.id : null} metrics={railMetrics} stations={stations} trains={trains} />
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const authentication = useAuthentication();

  if (!authentication.authenticated) {
    return (
      <LoginScreen
        loading={authentication.loading}
        submitting={authentication.submitting}
        error={authentication.error}
        onLogin={authentication.login}
        onRetry={authentication.retry}
      />
    );
  }

  return <SatisMapApplication />;
}
